import type { CombineOnItem } from '../types/Api';
import type { TimeProfile } from './calculateNodeTimeProfile';

const TIMESTAMP_TYPES = new Set(['timestamp', 'minute', 'hour', 'second']);
const DATE_TYPES = new Set(['date', 'day']);
const GASDAY_TYPES = new Set(['gasday']);

export interface CombineSuggestion {
  value?: string;
  reason?: string;
  confidence?: 'high' | 'medium' | 'low';
}

export function suggestCombineOn(
  leftProfile?: TimeProfile,
  rightProfile?: TimeProfile,
  availableOptions: CombineOnItem[] = [],
): CombineSuggestion {
  const available = new Set(
    availableOptions
      .map((option) => normalizeValue(option.value))
      .filter((value): value is string => Boolean(value))
  );
  const left = normalizeProfile(leftProfile);
  const right = normalizeProfile(rightProfile);

  if (!left || !right) {
    return {};
  }

  const leastStrict = pickLeastStrict([left.effective, right.effective], available);
  if (leastStrict) {
    const bothConcrete = !left.matchesAny && !right.matchesAny;
    return {
      value: leastStrict,
      reason: buildLeastStrictReason(left, right, leastStrict),
      confidence: bothConcrete ? 'high' : 'medium',
    };
  }

  const preferredFallback = firstAvailable([left.preferred, right.preferred], available);
  if (preferredFallback) {
    return {
      value: preferredFallback,
      reason: `Using available backend preference ${preferredFallback}.`,
      confidence: 'low',
    };
  }

  return {};
}

function normalizeProfile(profile?: TimeProfile) {
  if (!profile) return undefined;

  const preferred = normalizeValue(profile.preferred_combine_on);
  const kind = normalizeKind(profile.kind ?? profile.label);
  const resolution = normalizeValue(profile.resolution ?? profile.label);
  const matchesAny = Boolean(profile.matches_any);
  const effective =
    preferred ??
    combineOnFromKind(kind) ??
    combineOnFromResolution(resolution);

  if (!preferred && !kind && !resolution && !matchesAny) {
    return undefined;
  }

  return { preferred, kind, resolution, effective, matchesAny };
}

function firstAvailable(values: Array<string | undefined>, available: Set<string>): string | undefined {
  for (const value of values) {
    if (value && isAvailable(value, available)) {
      return value;
    }
  }
  return undefined;
}

function isAvailable(value: string, available?: Set<string>) {
  return !available || available.size === 0 || available.has(value);
}

function normalizeKind(value?: string): string | undefined {
  const normalized = normalizeValue(value);
  if (!normalized) return undefined;
  if (GASDAY_TYPES.has(normalized)) return 'gasday';
  if (DATE_TYPES.has(normalized)) return 'date';
  if (TIMESTAMP_TYPES.has(normalized)) return 'timestamp';
  return normalized;
}

function combineOnFromKind(kind?: string): string | undefined {
  if (kind === 'gasday') return 'gasday';
  if (kind === 'date') return 'date';
  if (kind === 'timestamp') return 'exact_match';
  return undefined;
}

function combineOnFromResolution(resolution?: string): string | undefined {
  if (!resolution) return undefined;
  if (GASDAY_TYPES.has(resolution)) return 'gasday';
  if (DATE_TYPES.has(resolution)) return 'date';
  if (TIMESTAMP_TYPES.has(resolution)) return 'exact_match';
  return undefined;
}

function pickLeastStrict(values: Array<string | undefined>, available: Set<string>): string | undefined {
  const candidates = values
    .filter((value): value is string => Boolean(value))
    .filter((value) => isAvailable(value, available));

  if (candidates.length === 0) {
    return undefined;
  }

  return candidates.reduce((leastStrict, current) =>
    getStrictness(current) < getStrictness(leastStrict) ? current : leastStrict
  );
}

function getStrictness(value: string): number {
  if (value === 'gasday') return 1;
  if (value === 'date') return 2;
  if (value === 'exact_match') return 3;
  return 99;
}

function buildLeastStrictReason(
  left: { effective?: string; matchesAny?: boolean },
  right: { effective?: string; matchesAny?: boolean },
  suggestion: string,
): string {
  if (left.matchesAny || right.matchesAny) {
    return `One source matches any timestamp, so ${suggestion} is the safest alignment.`;
  }
  if (left.effective && right.effective && left.effective !== right.effective) {
    return `Choosing the least strict supported alignment from ${left.effective} and ${right.effective}.`;
  }
  return `Both sources resolve to ${suggestion} from timing metadata.`;
}

function normalizeValue(value?: string): string | undefined {
  const normalized = value?.trim().toLowerCase();
  return normalized || undefined;
}
